import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import WysiwygEditor from '../../components/WysiwygEditor';
import api from '../../services/api';

import Swal from 'sweetalert2';
import {
  Pencil,
  Trash2,
  Plus
} from 'lucide-react';

function MechanicsList() {
  const navigate = useNavigate();
  const [mechanics, setMechanics] = useState([]);
  const [defaultMechanics, setDefaultMechanics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedMechanics, setSelectedMechanics] = useState([]);
  const [newMechanic, setNewMechanic] = useState({
    label: '',
    description: '',
    type: ''
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    fetchMechanics();
  }, []);

  const fetchMechanics = async () => {
    try {
      // Fetch all mechanics (including defaults)
      const allResponse = await api.get('/admin/mechanics/all');
      const allMechanics = allResponse.data.mechanics || [];
      
      // Separate default and custom mechanics
      setDefaultMechanics(allMechanics.filter(m => m.is_default));
      setMechanics(allMechanics.filter(m => !m.is_default));
    } catch (err) {
      console.error('Failed to fetch mechanics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateMechanic = async () => {
    if (!newMechanic.label) {
      await Swal.fire('Error', 'Please provide a mechanic name', 'error');
      return;
    }

    if (!newMechanic.type) {
      await Swal.fire('Error', 'Please select a type / category', 'error');
      return;
    }

    console.log('Creating mechanic with data:', newMechanic);

    try {
      await api.post('/admin/mechanics', newMechanic);
      setShowCreateModal(false);
      setNewMechanic({
        label: '',
        description: '',
        type: ''
      });
      fetchMechanics();
      setSuccessMessage('Mechanic created successfully!');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to create mechanic:', err);
      const error = err.response?.data?.error || err.response?.data?.details || 'Failed to create mechanic';
      setErrorMessage(error);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  };

  const handleDeleteMechanic = async (id, label) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      html: `You are about to delete "<strong>${label}</strong>"<br><br>Warning: This will affect all campaigns using this mechanic type.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/mechanics/${id}`);
        setSuccessMessage(`Successfully deleted "${label}"`);
        setShowSuccess(true);
        setTimeout(() => {
          setShowSuccess(false);
        }, 3000);
        fetchMechanics();
      } catch (err) {
        console.error('Failed to delete mechanic:', err);
        const error = err.response?.data?.error || 'Failed to delete mechanic';
        setErrorMessage(error);
        setShowError(true);
        setTimeout(() => {
          setShowError(false);
        }, 5000);
      }
    }
  };

  const handleSelectMechanic = (mechanicId) => {
    setSelectedMechanics(prev => {
      if (prev.includes(mechanicId)) {
        return prev.filter(id => id !== mechanicId);
      } else {
        return [...prev, mechanicId];
      }
    });
  };

  const handleSelectAll = () => {
    const allMechanics = [...defaultMechanics, ...mechanics];
    if (selectedMechanics.length === allMechanics.length) {
      setSelectedMechanics([]);
    } else {
      setSelectedMechanics(allMechanics.map(m => m.id));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedMechanics.length === 0) return;

    const result = await Swal.fire({
      title: 'Are you sure?',
      html: `You are about to delete <strong>${selectedMechanics.length}</strong> mechanics.<br><br>Warning: This will affect all campaigns using these mechanic types.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete all!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        await api.post('/admin/mechanics/bulk-delete', {
          mechanicIds: selectedMechanics
        });
        await Swal.fire(
          'Deleted!',
          `${selectedMechanics.length} mechanics have been deleted.`,
          'success'
        );
        setSelectedMechanics([]);
        fetchMechanics();
      } catch (err) {
        console.error('Bulk delete error:', err);
        const errorMessage = err.response?.data?.error || 'Failed to delete mechanics.';
        await Swal.fire('Error!', errorMessage, 'error');
      }
    }
  };

  const handleToggleActive = async (id, currentStatus) => {
    try {
      await api.patch(`/admin/mechanics/${id}/status`, { is_active: !currentStatus });
      fetchMechanics();
      setSuccessMessage('Mechanic status updated successfully!');
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Failed to update mechanic status:', err);
      const error = err.response?.data?.error || 'Failed to update mechanic status';
      setErrorMessage(error);
      setShowError(true);
      setTimeout(() => {
        setShowError(false);
      }, 5000);
    }
  };

  if (loading) {
    return (
      <AdminLayout activeSection="MECHANICS">
        <div className="flex items-center justify-center h-64">
          <div className="text-white">Loading...</div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout activeSection="MECHANICS">
      <style>
        {`
          @keyframes fadeInRight {
            from {
              opacity: 0;
              transform: translateX(20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .animate-fadeInRight {
            animation: fadeInRight 0.3s ease-out;
          }
        `}
      </style>

      {/* Success notification */}
      {showSuccess && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-green-50 border border-green-200 text-green-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{successMessage}</span>
          </div>
        </div>
      )}

      {/* Error notification */}
      {showError && (
        <div className="fixed top-4 right-4 z-50 animate-fadeInRight">
          <div className="bg-red-50 border border-red-200 text-red-800 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3">
            <svg className="h-5 w-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
            <span className="font-medium">{errorMessage}</span>
          </div>
        </div>
      )}

      <div className="mb-6">
        <h1 className="text-4xl font-bold text-white">MECHANICS</h1>
      </div>

      <div className="bg-white rounded-3xl shadow-2xl overflow-hidden" style={{ minHeight: '600px' }}>
        {/* Bulk Actions Bar */}
        {selectedMechanics.length > 0 && (
          <div className="bg-gray-100 px-6 py-4 flex items-center justify-between">
            <span className="text-sm font-medium text-gray-700">
              {selectedMechanics.length} mechanic{selectedMechanics.length > 1 ? 's' : ''} selected
            </span>
            <button
              onClick={handleBulkDelete}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
            >
              <Trash2 className="w-4 h-4 inline-block mr-1" />
              Delete Selected
            </button>
          </div>
        )}
        
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="bg-teal-800 text-white">
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider w-16">
                  <input
                    type="checkbox"
                    checked={(defaultMechanics.length + mechanics.length) > 0 && selectedMechanics.length === (defaultMechanics.length + mechanics.length)}
                    onChange={handleSelectAll}
                    className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500"
                  />
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  Mechanic Type
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-4 text-left text-sm font-medium uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-center text-sm font-medium uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {defaultMechanics.length === 0 && mechanics.length === 0 ? (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-sm text-gray-500">
                    No mechanics found. Add your first mechanic type.
                  </td>
                </tr>
              ) : (
                <>
                  {/* Default mechanics from database */}
                  {defaultMechanics.map((mechanic) => (
                    <tr key={mechanic.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={selectedMechanics.includes(mechanic.id)}
                          onChange={() => handleSelectMechanic(mechanic.id)}
                          className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{mechanic.label}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {mechanic.type ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 capitalize">
                            {mechanic.type.replace(/_/g, ' ')}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Not set</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="text-sm text-gray-500 line-clamp-2"
                          dangerouslySetInnerHTML={{
                            __html: mechanic.description || 'Built-in mechanic type'
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleActive(mechanic.id, mechanic.is_active)}
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer hover:opacity-80 ${
                            mechanic.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {mechanic.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => navigate(`/admin/mechanics/${mechanic.id}/edit`)}
                            className="text-teal-600 hover:text-teal-800"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteMechanic(mechanic.id, mechanic.label)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {/* Custom mechanics */}
                  {mechanics.map((mechanic) => (
                    <tr key={mechanic.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={selectedMechanics.includes(mechanic.id)}
                          onChange={() => handleSelectMechanic(mechanic.id)}
                          className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500"
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{mechanic.label}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {mechanic.type ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-800 capitalize">
                            {mechanic.type.replace(/_/g, ' ')}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-400">Not set</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="text-sm text-gray-500 line-clamp-2"
                          dangerouslySetInnerHTML={{
                            __html: mechanic.description || 'No description'
                          }}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => handleToggleActive(mechanic.id, mechanic.is_active)}
                          className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full cursor-pointer hover:opacity-80 ${
                            mechanic.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {mechanic.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        <div className="flex items-center justify-center space-x-2">
                          <button
                            onClick={() => navigate(`/admin/mechanics/${mechanic.id}/edit`)}
                            className="text-teal-600 hover:text-teal-800"
                            title="Edit"
                          >
                            <Pencil className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDeleteMechanic(mechanic.id, mechanic.label)}
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer with Add Button */}
        <div className="px-6 py-4 flex justify-between items-center">
          <button
            onClick={() => setShowCreateModal(true)}
            className="w-12 h-12 bg-teal-800 text-white rounded-full flex items-center justify-center hover:bg-teal-700 transition-colors shadow-lg"
            title="Add New Mechanic"
          >
            <Plus className="w-8 h-8" />
          </button>

          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">
              {defaultMechanics.length + mechanics.length} mechanics
            </span>
          </div>
        </div>
      </div>

      {/* Create Mechanic Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-8 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Create New Mechanic Type</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Mechanic Name *
                </label>
                <input
                  type="text"
                  value={newMechanic.label}
                  onChange={(e) => setNewMechanic({ ...newMechanic, label: e.target.value })}
                  placeholder="e.g., Weekend Bonus Entries"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type / Category *
                </label>
                <select
                  value={newMechanic.type}
                  onChange={(e) => setNewMechanic({ ...newMechanic, type: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                >
                  <option value="">Select a type...</option>
                  <option value="membership">Membership</option>
                  <option value="cashback">Cashback</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <WysiwygEditor
                  value={newMechanic.description}
                  onChange={(value) => setNewMechanic({ ...newMechanic, description: value })}
                  placeholder="Describe how this mechanic works, including rules, win conditions, and prize structure..."
                />
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="px-6 py-3 text-gray-700 bg-gray-200 rounded-xl hover:bg-gray-300 font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleCreateMechanic}
                className="px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 font-medium"
              >
                Create Mechanic
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

export default MechanicsList;